import { supabase } from '@/lib/supabase';

const BUCKET = 'product-photos';
const MAX_BYTES = 5 * 1024 * 1024; // 5 MB
const ALLOWED_MIME = ['image/jpeg', 'image/png', 'image/webp'];

/**
 * Upload manuel d'une photo produit (drag-drop ou bouton). Retourne le
 * path à stocker dans inventory_items.image_url. Génère un nom unique
 * pour éviter les collisions entre items.
 */
export async function uploadProductPhoto(file: File): Promise<string> {
  if (file.size > MAX_BYTES) {
    throw new Error(
      `Fichier trop gros (${Math.round(file.size / 1024)} KB). Max ${MAX_BYTES / 1024 / 1024} MB.`
    );
  }
  if (!ALLOWED_MIME.includes(file.type)) {
    throw new Error('Format non supporté. Utilise JPEG, PNG ou WEBP.');
  }

  const ext = file.name.split('.').pop()?.toLowerCase() ?? 'jpg';
  const safeExt = ['jpg', 'jpeg', 'png', 'webp'].includes(ext) ? ext : 'jpg';
  const path = `${crypto.randomUUID()}.${safeExt}`;

  const { error } = await supabase.storage.from(BUCKET).upload(path, file, {
    cacheControl: '3600',
    upsert: false,
    contentType: file.type,
  });
  if (error) throw error;
  return path;
}

export interface PhotoTransform {
  /** Largeur cible en px (Supabase resize cover par défaut). */
  width?: number;
  /** Hauteur cible en px. */
  height?: number;
  /** Qualité 0-100 (défaut 75 = bon compromis taille/perception). */
  quality?: number;
}

/**
 * URL publique permanente d'une photo produit. Le bucket est public
 * donc pas de round-trip nécessaire.
 *
 * Accepte un paramètre `transform` pour servir une version resize
 * + compressée via Supabase Storage Image Transformations (économise
 * 70-90% de bande passante sur les originaux 2-5 MB).
 */
export function getProductPhotoPublicUrl(
  photoPath: string | null | undefined,
  transform?: PhotoTransform
): string | null {
  if (!photoPath) return null;
  const options = transform
    ? {
        transform: {
          width: transform.width,
          height: transform.height,
          quality: transform.quality ?? 75,
          resize: 'contain' as const,
        },
      }
    : undefined;
  const { data } = supabase.storage.from(BUCKET).getPublicUrl(photoPath, options);
  return data?.publicUrl ?? null;
}

/**
 * Supprime une photo du bucket. Tolérant aux échecs.
 */
export async function deleteProductPhoto(
  photoPath: string | null | undefined
): Promise<void> {
  if (!photoPath) return;
  const { error } = await supabase.storage.from(BUCKET).remove([photoPath]);
  if (error) {
    console.warn('Échec suppression photo produit :', error);
  }
}
